import React from "react";
import moment from "moment";
import { formatOrderDate } from "../../../../lib/formatOrderDate";
import Modal from "react-modal";
import { BsPencil, BsTrash, BsEnvelope, BsClipboardPlus, BsCreditCard2Back, BsImage } from "react-icons/bs";
import { Oval } from "react-loader-spinner";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  DumpsterSize,
  MaterialDisposing,
  Order,
  OrderDetailsInfo,
  PaymentType,
  dumpsterSizeToText,
  materialDisposingToText,
  paymentTypeToText,
} from "../../../../models";
import {
  setShowDeleteOrderModal,
  setShowUpdateOrderModal,
  setShowPickupInstructionsModal,
  setShowUpdatePaymentTypeModal,
  sendConfirmationEmail,
} from "../../ordersSlice";
import { triplePSDatasource } from "../../../../datasource";

import "./OrderDetailsCard.scss";

export interface OrderDetailsCardProps {
  order: Order;
  orderDetails?: OrderDetailsInfo;
}

export function OrderDetailsCard(props: OrderDetailsCardProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const cancelOrder = useAppSelector((state) => state.cancelOrder.cancelOrder);
  const sendConfirmationEmailAPI = useAppSelector(
    (state) => state.orders.sendConfirmationEmailAPI
  );

  const { order, orderDetails } = props;

  // Delivery photo viewer (lazy-loaded: only fetched on first click)
  const [photoModalOpen, setPhotoModalOpen] = React.useState(false);
  const [photoDataUrl, setPhotoDataUrl] = React.useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = React.useState(false);

  const hasDeliveryPhoto = !!orderDetails?.delivery?.photo_s3_location;
  const deliveryId = orderDetails?.delivery?.delivery_id;

  const onViewDeliveryPhotoClick = async () => {
    if (!deliveryId) return;
    setPhotoModalOpen(true);
    if (!photoDataUrl) {
      setPhotoLoading(true);
      try {
        const base64 = await triplePSDatasource.getDeliveryPhoto(deliveryId);
        setPhotoDataUrl(`data:image/jpeg;base64,${base64}`);
      } catch {
        toast.error("Failed to load delivery photo.");
      } finally {
        setPhotoLoading(false);
      }
    }
  };

  // Pickup photo viewer (lazy-loaded: only fetched on first click)
  const [pickupPhotoModalOpen, setPickupPhotoModalOpen] = React.useState(false);
  const [pickupPhotoDataUrl, setPickupPhotoDataUrl] = React.useState<string | null>(null);
  const [pickupPhotoLoading, setPickupPhotoLoading] = React.useState(false);

  const hasPickupPhoto = !!orderDetails?.pickup?.photo_s3_location;
  const pickupId = orderDetails?.pickup?.pickup_id;

  const onViewPickupPhotoClick = async () => {
    if (!pickupId) return;
    setPickupPhotoModalOpen(true);
    if (!pickupPhotoDataUrl) {
      setPickupPhotoLoading(true);
      try {
        const base64 = await triplePSDatasource.getPickupPhoto(pickupId);
        setPickupPhotoDataUrl(`data:image/jpeg;base64,${base64}`);
      } catch {
        toast.error("Failed to load pickup photo.");
      } finally {
        setPickupPhotoLoading(false);
      }
    }
  };

  React.useEffect(() => {
    if (!cancelOrder.loading && !!cancelOrder.value) {
      navigate("/orders");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelOrder.loading, !!cancelOrder.value]);

  React.useEffect(() => {
    if (!sendConfirmationEmailAPI.loading && !!sendConfirmationEmailAPI.error) {
      toast.error("Something went wrong. Please try again.");
    }

    if (!sendConfirmationEmailAPI.loading && !!sendConfirmationEmailAPI.value) {
      toast.success("Successfully send confirmation email.");
      dispatch(setShowUpdateOrderModal(undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sendConfirmationEmailAPI.loading,
    !!sendConfirmationEmailAPI.error,
    !!sendConfirmationEmailAPI.value,
  ]);

  const onCancelOrderClick = () => {
    dispatch(setShowDeleteOrderModal(order!.id));
  };

  const onEditOrderClick = () => {
    dispatch(setShowUpdateOrderModal(order));
  };

  const onAddPickupInstructions = () => {
    dispatch(setShowPickupInstructionsModal(order.id));
  };

  const onConfirmationEmailClick = () => {
    if (!order) return;

    dispatch(
      sendConfirmationEmail({
        id: order.id,
      })
    );
  };

  const onUpdatePaymentTypeClick = () => {
    dispatch(setShowUpdatePaymentTypeModal(order));
  };

  return (
    <div className="order-details-card-component">
      <div className="header">
        <div className="header-title">Order Details</div>

        <div className="buttons-container">
          <div className="button-container" onClick={onCancelOrderClick}>
            <BsTrash className="icon" />
            <div>Cancel Order</div>
          </div>

          <div className="button-container" onClick={onEditOrderClick}>
            <BsPencil className="icon" />
            <div>Update Order</div>
          </div>

          <div className="button-container" onClick={onAddPickupInstructions}>
            <BsClipboardPlus className="icon" />
            <div>Pickup Instructions</div>
          </div>

          <div className="button-container" onClick={onConfirmationEmailClick}>
            <BsEnvelope className="icon" />
            <div>Send Confirmaion</div>
            {sendConfirmationEmailAPI.loading && (
              <div style={{ marginLeft: 8 }}>
                <Oval
                  color="#131313"
                  height={20}
                  width={20}
                  secondaryColor="#FBD20A"
                />
              </div>
            )}
          </div>

          <div className="button-container" onClick={onUpdatePaymentTypeClick}>
            <BsCreditCard2Back className="icon" />
            <div>Update Payment Type</div>
          </div>
        </div>
      </div>

      <div className="details-container">
        <div className="column">
          <div className="item-container">
            <div className="label">Status:</div>
            <div className="value">{order.status}</div>
          </div>

          <div className="item-container">
            <div className="label">Address:</div>
            <div className="value">{order.address}</div>
          </div>

          <div className="item-container">
            <div className="label">City:</div>
            <div className="value">{order.city}</div>
          </div>

          <div className="item-container">
            <div className="label">Zip Code:</div>
            <div className="value">{order.zip}</div>
          </div>

          <div className="item-container">
            <div className="label">Service Type:</div>
            <div className="value">{order.service}</div>
          </div>

          <div className="item-container">
            <div className="label">Dumpster Size:</div>
            <div className="value">
              {dumpsterSizeToText[order.dumpsterSize as DumpsterSize]}
            </div>
          </div>

          <div className="item-container">
            <div className="label">Dumpster:</div>
            <div className="value">
              {order.dumpsterId ? (
                <Link
                  to={`/dumpsters/${order.dumpsterId}`}
                  className="order-link"
                >
                  {order.dumpsterLabel}
                </Link>
              ) : (
                order.dumpsterLabel || "NA"
              )}
            </div>
          </div>

          <div className="item-container">
            <div className="label">Material Disposing:</div>
            <div className="value">
              {
                materialDisposingToText[
                  order.materialDisposing as MaterialDisposing
                ]
              }
            </div>
          </div>

          <div className="item-container">
            <div className="label">Description:</div>
            <div className="value">{order.description || "NA"}</div>
          </div>

          <div className="item-container">
            <div className="label">Special Instructions:</div>
            <div className="value">{order.specialInstructions || "NA"}</div>
          </div>
        </div>

        <div className="column">
          <div className="item-container">
            <div className="label">Delivery Date:</div>
            <div className="value">
              {formatOrderDate(order.deliveryDate)}
            </div>
          </div>

          {orderDetails?.delivery && (
            <>
              <div className="item-container">
                <div className="label">Delivery Status:</div>
                <div className="value">{orderDetails.delivery.status}</div>
              </div>
              {orderDetails.delivery.driverName && (
                <div className="item-container">
                  <div className="label">Delivery Driver:</div>
                  <div className="value">{orderDetails.delivery.driverName}</div>
                </div>
              )}
              {orderDetails.delivery.completedAt && (
                <div className="item-container">
                  <div className="label">Delivered At:</div>
                  <div className="value">
                    {moment(orderDetails.delivery.completedAt).format("lll")}
                  </div>
                </div>
              )}
              {hasDeliveryPhoto && (
                <div className="item-container">
                  <div
                    className="button-container"
                    style={{ width: "auto", paddingLeft: 8, paddingRight: 8 }}
                    onClick={onViewDeliveryPhotoClick}
                  >
                    <BsImage className="icon" />
                    <div>View delivery photo</div>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="item-container">
            <div className="label">Pickup Date:</div>
            <div className="value">{formatOrderDate(order.pickupDate)}</div>
          </div>

          {orderDetails?.pickup && (
            <>
              <div className="item-container">
                <div className="label">Pickup Status:</div>
                <div className="value">{orderDetails.pickup.status}</div>
              </div>
              {orderDetails.pickup.driverName && (
                <div className="item-container">
                  <div className="label">Pickup Driver:</div>
                  <div className="value">{orderDetails.pickup.driverName}</div>
                </div>
              )}
              {orderDetails.pickup.completedAt && (
                <div className="item-container">
                  <div className="label">Picked Up At:</div>
                  <div className="value">
                    {moment(orderDetails.pickup.completedAt).format("lll")}
                  </div>
                </div>
              )}
              {hasPickupPhoto && (
                <div className="item-container">
                  <div
                    className="button-container"
                    style={{ width: "auto", paddingLeft: 8, paddingRight: 8 }}
                    onClick={onViewPickupPhotoClick}
                  >
                    <BsImage className="icon" />
                    <div>View pickup photo</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="column">
          <div className="item-container">
            <div className="label">Base Price:</div>
            <div className="value">${order.price}</div>
          </div>

          <div className="item-container">
            <div className="label">Delivery Fee:</div>
            <div className="value">${order.deliveryFee}</div>
          </div>

          <div className="item-container">
            <div className="label">Discount:</div>
            <div className="value">${order.discount}</div>
          </div>

          <div className="item-container">
            <div className="label">Additional charges:</div>
            <div className="value">${order.additionalCharges}</div>
          </div>

          <div className="item-container">
            <div className="label">Total Price:</div>
            <div className="value bold">
              $
              {order.price +
                order.deliveryFee +
                order.additionalCharges -
                order.discount}
            </div>
          </div>

          <div className="item-container">
            <div className="label">Payment Type:</div>
            <div className="value">
              {order.paymentType
                ? paymentTypeToText[order.paymentType as PaymentType]
                : "NA"}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={photoModalOpen}
        onRequestClose={() => setPhotoModalOpen(false)}
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#FFFFFF",
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
          },
          overlay: { zIndex: 1000 },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 600 }}>Delivery Photo</div>
            <button
              aria-label="Close photo viewer"
              onClick={() => setPhotoModalOpen(false)}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18 }}
            >
              &#x2715;
            </button>
          </div>
          {photoLoading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <Oval color="#131313" height={40} width={40} secondaryColor="#FBD20A" />
            </div>
          )}
          {!photoLoading && photoDataUrl && (
            <img
              src={photoDataUrl}
              alt="Delivery photo"
              style={{ maxWidth: "80vw", maxHeight: "75vh", objectFit: "contain" }}
            />
          )}
        </div>
      </Modal>

      <Modal
        isOpen={pickupPhotoModalOpen}
        onRequestClose={() => setPickupPhotoModalOpen(false)}
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#FFFFFF",
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
          },
          overlay: { zIndex: 1000 },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 600 }}>Pickup Photo</div>
            <button
              aria-label="Close pickup photo viewer"
              onClick={() => setPickupPhotoModalOpen(false)}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18 }}
            >
              &#x2715;
            </button>
          </div>
          {pickupPhotoLoading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <Oval color="#131313" height={40} width={40} secondaryColor="#FBD20A" />
            </div>
          )}
          {!pickupPhotoLoading && pickupPhotoDataUrl && (
            <img
              src={pickupPhotoDataUrl}
              alt="Pickup photo"
              style={{ maxWidth: "80vw", maxHeight: "75vh", objectFit: "contain" }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
